import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, clearToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Me() {
  const [me, setMe] = useState(null);
  const [loc, setLoc] = useState(null);
  const [options, setOptions] = useState([]);
  const [saved, setSaved] = useState(null);
  const [digestTopN, setDigestTopN] = useState("");
  const [digestStatus, setDigestStatus] = useState(null);
  const [myDigest, setMyDigest] = useState(null);
  const [digestDay, setDigestDay] = useState("mon");
  const [away, setAway] = useState(null);
  const [awayForm, setAwayForm] = useState({ delegate_email: "", until: "" });
  const [awayStatus, setAwayStatus] = useState(null);
  const [devices, setDevices] = useState(null);
  const [subs, setSubs] = useState(null);
  const [notes, setNotes] = useState(null);
  const [followKey, setFollowKey] = useState("");
  const [fault, setFault] = useState({ id: null, description: "" });
  const [faultStatus, setFaultStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiFetch("/auth/me").then(async (r) => {
      if (r.ok) setMe(await r.json());
      else {
        setError("Not signed in");
        navigate("/login");
      }
    });
    apiFetch("/me/location").then((r) => (r.ok ? r.json() : null)).then(setLoc);
    apiFetch("/locations").then((r) => (r.ok ? r.json() : [])).then(setOptions);
    apiFetch("/me/preferences").then((r) => (r.ok ? r.json() : { preferences: {} }))
      .then((d) => setDigestTopN(d.preferences?.digest_top_n ?? ""));
    apiFetch("/me/devices").then((r) => (r.ok ? r.json() : null)).then(setDevices);
    apiFetch("/me/subscriptions").then((r) => (r.ok ? r.json() : null)).then(setSubs);
    apiFetch("/notifications").then((r) => (r.ok ? r.json() : [])).then(setNotes);
    apiFetch("/digest/status/mine").then((r) => (r.ok ? r.json() : null)).then((d) => {
      setMyDigest(d);
      if (d?.delivery_day) setDigestDay(d.delivery_day);
    });
    apiFetch("/me/away").then((r) => (r.ok ? r.json() : null)).then(setAway);
  }, [navigate]);

  const loadNotes = () =>
    apiFetch("/notifications").then((r) => (r.ok ? r.json() : [])).then(setNotes);

  const markRead = (id) =>
    apiFetch(`/notifications/${id}/read`, { method: "POST" }).then(loadNotes);

  const followChange = (method, key) =>
    apiFetch(method === "POST" ? "/me/subscriptions" : `/me/subscriptions/${key}`,
             method === "POST" ? { method, body: JSON.stringify({ service_key: key }) }
                               : { method })
      .then(() => apiFetch("/me/subscriptions"))
      .then((r) => (r.ok ? r.json() : null))
      .then(setSubs);

  const reportFault = async (asset) => {
    const r = await apiFetch(`/me/devices/${asset.id}/fault`, {
      method: "POST", body: JSON.stringify({ description: fault.description }),
    });
    const body = await r.json().catch(() => ({}));
    setFaultStatus(r.ok ? body.message
      : (typeof body.detail === "string" ? body.detail : "Could not report that device"));
    setFault({ id: null, description: "" });
    apiFetch("/me/devices").then((res) => (res.ok ? res.json() : null)).then(setDevices);
  };

  const saveDigest = async () => {
    const value = digestTopN === "" ? null : Number(digestTopN);
    const r = await apiFetch("/me/preferences", {
      method: "PUT", body: JSON.stringify({ preferences: { digest_top_n: value } }),
    });
    const body = await r.json().catch(() => ({}));
    setDigestStatus(r.ok
      ? (value ? `Your digest will list up to ${value} problems` : "Using the policy default")
      : (typeof body.detail === "string" ? body.detail : "Could not save digest size"));
  };

  const saveDigestDay = async (day) => {
    setDigestDay(day);
    const r = await apiFetch("/me/preferences", {
      method: "PUT", body: JSON.stringify({ preferences: { status_digest_day: day } }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setDigestStatus(typeof body.detail === "string" ? body.detail
                                                      : "Could not save that day");
      return;
    }
    const refreshed = await apiFetch("/digest/status/mine");
    if (refreshed.ok) setMyDigest(await refreshed.json());
  };

  const saveAway = async () => {
    const r = await apiFetch("/me/away", {
      method: "POST", body: JSON.stringify(awayForm),
    });
    const body = await r.json().catch(() => ({}));
    setAwayStatus(r.ok ? body.message
      : (typeof body.detail === "string" ? body.detail : "Could not set your cover"));
    if (r.ok) {
      setAwayForm({ delegate_email: "", until: "" });
      apiFetch("/me/away").then((res) => (res.ok ? res.json() : null)).then(setAway);
    }
  };

  const endAway = async () => {
    const r = await apiFetch("/me/away", { method: "DELETE" });
    const body = await r.json().catch(() => ({}));
    setAwayStatus(r.ok ? body.message : "Could not end your cover");
    apiFetch("/me/away").then((res) => (res.ok ? res.json() : null)).then(setAway);
  };

  const saveLocation = async (key) => {
    const r = await apiFetch("/me/location", {
      method: "PUT", body: JSON.stringify({ location_key: key }),
    });
    if (r.ok) {
      setSaved("Location updated");
      apiFetch("/me/location").then((res) => (res.ok ? res.json() : null)).then(setLoc);
    } else {
      const body = await r.json().catch(() => ({}));
      setSaved(body.detail || "Could not update location");
    }
  };

  if (error) return null;
  if (!me) return <div className="p-8" data-testid="me-loading">Loading…</div>;

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-lg" data-testid="me-card">
        <CardHeader>
          <h1 className="text-sm text-muted-foreground">Your profile</h1>
          <CardTitle className="text-2xl" data-testid="me-name">{me.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div data-testid="me-email"><span className="text-muted-foreground">Email:</span> {me.email}</div>
          <div data-testid="me-identity">
            <span className="text-muted-foreground">Identity:</span> {me.provider} / {me.external_id}
          </div>
          <div data-testid="me-roles">
            <span className="text-muted-foreground">Roles:</span>{" "}
            {me.roles.length ? me.roles.join(", ") : "none"}
          </div>
          <div data-testid="me-active">
            <span className="text-muted-foreground">Status:</span> {me.active ? "Active" : "Deactivated"}
          </div>

          {loc && (
            <div className="border-t pt-3 space-y-2" data-testid="me-location-block">
              <label className="text-muted-foreground block" htmlFor="me-location-select">
                Location
              </label>
              <p data-testid="me-location">
                {loc.location ? `${loc.location.name} (${loc.location.code})` : "Not set"}
                {loc.source && (
                  <span className="text-muted-foreground"> · from {loc.source}</span>
                )}
              </p>
              {loc.editable_here ? (
                <select
                  id="me-location-select"
                  data-testid="me-location-select"
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={loc.location_key || ""}
                  onChange={(e) => saveLocation(e.target.value || null)}
                >
                  <option value="">Not set</option>
                  {options.map((o) => (
                    <option key={o.key} value={o.key}>{o.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="me-location-locked">
                  {loc.reason}
                </p>
              )}
              {saved && <p className="text-xs" data-testid="me-location-status">{saved}</p>}
            </div>
          )}
          <div className="border-t pt-3 space-y-2" data-testid="me-digest-block">
            <label className="text-muted-foreground block" htmlFor="me-digest-input">
              Weekly recurrence digest size
            </label>
            <p className="text-xs text-muted-foreground">
              How many repeat problems your weekly note lists, up to the maximum the digest
              policy allows.
            </p>
            <input
              id="me-digest-input"
              data-testid="me-digest-input"
              type="number"
              min="1"
              className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              value={digestTopN}
              onChange={(e) => setDigestTopN(e.target.value)}
            />
            <Button size="sm" variant="outline" data-testid="me-digest-save"
                    onClick={saveDigest}>Save digest size</Button>
            {digestStatus && (
              <p className="text-xs" data-testid="me-digest-status">{digestStatus}</p>
            )}
          </div>
          <div className="border-t pt-3 space-y-2" data-testid="me-away-block">
            <h2 className="text-muted-foreground">If you are away</h2>
            {away && (
              <>
                <p className="text-xs text-muted-foreground">{away.method}</p>
                {away.away ? (
                  <>
                    <p className="text-sm" data-testid="me-away-current">
                      {away.delegate?.email} is covering your approvals until{" "}
                      {String(away.until).slice(0, 10)}.
                    </p>
                    <Button size="sm" variant="outline" data-testid="me-away-end"
                            onClick={endAway}>I am back</Button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <label htmlFor="me-away-delegate" className="text-xs">
                      Who should cover your approvals?
                    </label>
                    <input id="me-away-delegate" data-testid="me-away-delegate" list="colleagues"
                           placeholder="Their work email"
                           className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                           value={awayForm.delegate_email}
                           onChange={(e) => setAwayForm({ ...awayForm,
                                                          delegate_email: e.target.value })} />
                    <datalist id="colleagues">
                      {away.colleagues.map((c) => (
                        <option key={c.email} value={c.email}>{c.name || c.email}</option>
                      ))}
                    </datalist>
                    <label htmlFor="me-away-until" className="text-xs">Away until</label>
                    <input id="me-away-until" type="date" data-testid="me-away-until"
                           className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                           value={awayForm.until}
                           onChange={(e) => setAwayForm({ ...awayForm,
                                                          until: e.target.value })} />
                    <Button size="sm" variant="outline" data-testid="me-away-save"
                            disabled={!awayForm.delegate_email || !awayForm.until}
                            onClick={saveAway}>Set my cover</Button>
                  </div>
                )}
                {awayStatus && (
                  <p className="text-xs" data-testid="me-away-status">{awayStatus}</p>
                )}
              </>
            )}
          </div>

          {myDigest && (
            <div className="border-t pt-3 space-y-2" data-testid="me-status-digest-block">
              <h2 className="text-muted-foreground">Your weekly status note</h2>
              <p className="text-xs text-muted-foreground" data-testid="me-status-digest-reason">
                {myDigest.reason}
              </p>
              <label htmlFor="me-status-digest-day" className="text-xs">Send it on</label>
              <select id="me-status-digest-day" data-testid="me-status-digest-day"
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      value={digestDay} onChange={(e) => saveDigestDay(e.target.value)}>
                {myDigest.day_choices.map((d) => (
                  <option key={d} value={d}>
                    {d === "off" ? "Do not send it"
                                 : `${myDigest.day_names?.[d] || d}s`}
                  </option>
                ))}
              </select>
              <ul className="text-xs space-y-1" data-testid="me-status-digest-preview">
                {myDigest.rendered.map((line, i) => (
                  <li key={i} className={i === 0 ? "font-medium" : "text-muted-foreground"}>
                    {line}
                  </li>
                ))}
                {myDigest.rendered.length === 0 && (
                  <li className="text-muted-foreground" data-testid="me-status-digest-empty">
                    Follow a service on this page to start receiving the weekly note.
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="border-t pt-3 space-y-2" data-testid="me-devices-block">
            <h2 className="text-muted-foreground">Your devices</h2>
            {devices ? (
              <>
                <p className="text-xs text-muted-foreground">{devices.note}</p>
                <ul className="space-y-2" data-testid="me-devices-list">
                  {devices.devices.map((d) => (
                    <li key={d.id} className="border rounded-md p-2 space-y-1"
                        data-testid={`me-device-${d.asset_tag}`}>
                      <p className="text-sm">
                        <span className="font-mono">{d.asset_tag}</span>{" "}
                        {[d.manufacturer, d.model].filter(Boolean).join(" ")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(d.status || "").replace(/_/g, " ")}
                        {d.location_key && ` · ${d.location_key}`}
                        {d.warranty_expiry && ` · warranty ${d.warranty_expiry}`}
                        {d.fault_request_number && ` · request ${d.fault_request_number}`}
                      </p>
                      {d.status === "in_repair" ? (
                        <p className="text-xs text-amber-800"
                           data-testid={`me-device-repair-${d.asset_tag}`}>
                          With IT for repair.
                        </p>
                      ) : fault.id === d.id ? (
                        <div className="space-y-1">
                          <label htmlFor={`fault-${d.asset_tag}`} className="text-xs">
                            What is wrong with it?
                          </label>
                          <input id={`fault-${d.asset_tag}`}
                                 data-testid={`me-device-fault-input-${d.asset_tag}`}
                                 className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                                 value={fault.description}
                                 onChange={(e) => setFault({ ...fault,
                                                             description: e.target.value })} />
                          <Button size="sm" variant="outline"
                                  data-testid={`me-device-fault-confirm-${d.asset_tag}`}
                                  onClick={() => reportFault(d)}>
                            Raise an IT request
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline"
                                data-testid={`me-device-fault-${d.asset_tag}`}
                                onClick={() => setFault({ id: d.id, description: "" })}>
                          Report a fault
                        </Button>
                      )}
                    </li>
                  ))}
                  {devices.devices.length === 0 && (
                    <li className="text-xs text-muted-foreground" data-testid="me-devices-empty">
                      No devices are recorded against you.
                    </li>
                  )}
                </ul>
                {faultStatus && (
                  <p className="text-xs" data-testid="me-device-fault-status">{faultStatus}</p>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="me-devices-loading">
                Loading your devices…
              </p>
            )}
          </div>
          <div className="border-t pt-3 space-y-2" data-testid="me-subscriptions-block">
            <h2 className="text-muted-foreground">Services you follow</h2>
            {subs ? (
              <>
                <p className="text-xs text-muted-foreground">{subs.note}</p>
                <ul className="space-y-1" data-testid="me-subscriptions-list">
                  {subs.following.map((s) => (
                    <li key={s.service_key} className="flex items-center justify-between gap-2"
                        data-testid={`me-subscription-${s.service_key}`}>
                      <span className="text-sm">
                        {s.name}
                        {s.currently_down ? (
                          <span className="text-xs text-red-800">
                            {" "}— down now ({s.outage.number})
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-800"> — running</span>
                        )}
                      </span>
                      <Button size="sm" variant="ghost"
                              data-testid={`me-unfollow-${s.service_key}`}
                              onClick={() => followChange("DELETE", s.service_key)}>
                        Unfollow
                      </Button>
                    </li>
                  ))}
                  {subs.following.length === 0 && (
                    <li className="text-xs text-muted-foreground"
                        data-testid="me-subscriptions-empty">
                      You are not following any service yet.
                    </li>
                  )}
                </ul>
                <div className="flex items-end gap-2">
                  <div className="flex-1 space-y-1">
                    <label htmlFor="me-follow-select" className="text-xs">Follow a service</label>
                    <select id="me-follow-select" data-testid="me-follow-select"
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            value={followKey} onChange={(e) => setFollowKey(e.target.value)}>
                      <option value="">Choose a service…</option>
                      {subs.available.map((s) => (
                        <option key={s.service_key} value={s.service_key}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <Button size="sm" variant="outline" data-testid="me-follow-submit"
                          disabled={!followKey}
                          onClick={() => { followChange("POST", followKey); setFollowKey(""); }}>
                    Follow
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="me-subscriptions-loading">
                Loading the services you follow…
              </p>
            )}
          </div>
          <div className="border-t pt-3 space-y-2" data-testid="me-notifications-block">
            <h2 className="text-muted-foreground">
              What you have been told
              {notes && notes.filter((n) => n.status !== "read").length > 0 && (
                <span className="ml-2 text-xs text-amber-800" data-testid="me-notifications-badge">
                  {notes.filter((n) => n.status !== "read").length} unread
                </span>
              )}
            </h2>
            {notes ? (
              <ul className="space-y-1" data-testid="me-notifications-list">
                {notes.slice(0, 15).map((n) => (
                  <li key={n.id} className="border rounded-md p-2"
                      data-testid={`notification-item-${n.id}`}>
                    <p className="text-sm">
                      {n.title}
                      {n.status !== "read" && (
                        <span className="ml-2 text-xs text-amber-800">new</span>
                      )}
                    </p>
                    {n.body && (
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap">{n.body}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {n.class} · {new Date(n.created_at).toLocaleString()}
                    </p>
                    {n.status !== "read" && (
                      <Button size="sm" variant="ghost"
                              data-testid={`notification-read-${n.id}`}
                              onClick={() => markRead(n.id)}>
                        Mark as read
                      </Button>
                    )}
                  </li>
                ))}
                {notes.length === 0 && (
                  <li className="text-xs text-muted-foreground"
                      data-testid="me-notifications-empty">
                    Nothing to tell you yet.
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="me-notifications-loading">
                Loading your notifications…
              </p>
            )}
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            onClick={() => { clearToken(); navigate("/login"); }}
          >
            Sign out
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
